// ============================================================
// HV-TaskFlow V1.1.0 — Đăng ký nhận thông báo đẩy (Web Push).
//
// RÀNG BUỘC CỦA iOS (bắt buộc phải hiểu, nếu không sẽ "không hiểu sao không chạy"):
//   1. Push CHỈ hoạt động khi PWA đã được "Thêm vào Màn hình chính".
//      Mở trong tab Safari thì KHÔNG BAO GIỜ nhận được push — không có cách lách.
//   2. Hộp thoại xin quyền CHỈ hiện được khi do người dùng BẤM NÚT.
//      Gọi tự động lúc load trang sẽ thất bại im lặng.
//   → Vì vậy luồng đúng là: kiểm tra đã cài chưa → nếu chưa thì hướng dẫn cài,
//     nếu rồi thì hiện nút "Bật thông báo" để người dùng tự bấm.
// ============================================================
import { apiClient } from './apiClient';

export type PushSupport =
  | 'ready'              // Đăng ký được ngay
  | 'needs-install'      // iOS: phải "Thêm vào Màn hình chính" trước
  | 'denied'             // Người dùng đã từ chối quyền
  | 'unsupported';       // Trình duyệt không hỗ trợ

/** iOS Safari đặt navigator.standalone; các trình duyệt khác dùng display-mode. */
export function isStandalone(): boolean {
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone;
  return (
    iosStandalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function detectPlatform(): string {
  if (isIos()) return 'ios';
  if (/Android/i.test(navigator.userAgent)) return 'android';
  return 'desktop';
}

/** Kiểm tra trạng thái trước khi hiện UI — để không mời gọi một thứ chắc chắn fail. */
export function getPushSupport(): PushSupport {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    // iOS chưa cài ra màn hình chính thì PushManager cũng không tồn tại
    if (isIos() && !isStandalone()) return 'needs-install';
    return 'unsupported';
  }
  if (isIos() && !isStandalone()) return 'needs-install';
  if (Notification.permission === 'denied') return 'denied';
  return 'ready';
}

/** ArrayBuffer → base64url, để so với khóa VAPID server trả về. */
function bufferToBase64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let raw = '';
  for (let i = 0; i < bytes.length; i++) raw += String.fromCharCode(bytes[i]);
  return window.btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Chuẩn hóa khóa về base64url không padding, để so sánh không bị lệch định dạng. */
function normalizeKey(key: string): string {
  return key.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Subscription cũ có đang dùng ĐÚNG khóa VAPID hiện tại không?
 *
 * VÌ SAO CẦN: subscription được ký bằng khóa VAPID tại thời điểm tạo. Nếu server
 * đổi cặp khóa (hoặc trước đó chưa cấu hình khóa), subscription cũ vẫn tồn tại
 * trên máy nhưng push service sẽ trả 403 Forbidden — biểu hiện là "bật thông báo
 * thành công mà không bao giờ nhận được gì".
 *
 * Safari không phải lúc nào cũng expose options.applicationServerKey → không đọc
 * được thì coi như KHÔNG khớp và đăng ký lại cho chắc.
 */
function isSubscriptionKeyCurrent(sub: PushSubscription, vapidKey: string): boolean {
  try {
    const existing = sub.options?.applicationServerKey;
    if (!existing) return false;
    return bufferToBase64Url(existing as ArrayBuffer) === normalizeKey(vapidKey);
  } catch {
    return false;
  }
}

/** Gỡ subscription cũ ở cả server lẫn máy. Lỗi thì bỏ qua — không được chặn luồng. */
async function dropStaleSubscription(sub: PushSubscription): Promise<void> {
  try {
    await apiClient.post('/push/unsubscribe', { endpoint: sub.endpoint });
  } catch {
    /* server có thể đã thu hồi rồi */
  }
  try {
    await sub.unsubscribe();
  } catch {
    /* bỏ qua */
  }
}

/** Lấy khóa VAPID công khai từ server (an toàn khi lộ). */
async function getVapidPublicKey(): Promise<string | null> {
  try {
    const res = await apiClient.get<{ vapidPublicKey: string | null }>('/app/info');
    return res.data.vapidPublicKey ?? null;
  } catch {
    return null;
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Bật thông báo. PHẢI được gọi từ sự kiện click của người dùng (ràng buộc iOS).
 * Trả về true nếu đăng ký thành công.
 */
export async function enablePush(): Promise<boolean> {
  if (getPushSupport() !== 'ready') return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const vapidKey = await getVapidPublicKey();
  if (!vapidKey) {
    // Server chưa cấu hình khóa VAPID → không đăng ký được.
    console.warn('[Push] Server chưa cấu hình khóa VAPID.');
    return false;
  }

  const reg = await navigator.serviceWorker.ready;

  // Đã có subscription cũ → CHỈ dùng lại nếu ký bằng đúng khóa VAPID hiện tại.
  // Khóa lệch (server vừa đổi khóa, hoặc trước đó chưa cấu hình) → gỡ và tạo mới,
  // nếu không push sẽ bị từ chối 403 mà người dùng không hề biết.
  let sub = await reg.pushManager.getSubscription();
  if (sub && !isSubscriptionKeyCurrent(sub, vapidKey)) {
    console.warn('[Push] Subscription cũ dùng khóa VAPID khác — đăng ký lại.');
    await dropStaleSubscription(sub);
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true, // bắt buộc: mọi push đều phải hiện cho người dùng thấy
      applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
    });
  }

  const json = sub.toJSON();
  await apiClient.post('/push/subscribe', {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh ?? '',
    auth: json.keys?.auth ?? '',
    platform: detectPlatform(),
  });

  return true;
}

/** Tắt thông báo trên thiết bị này. */
export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;

  try {
    await apiClient.post('/push/unsubscribe', { endpoint: sub.endpoint });
  } finally {
    await sub.unsubscribe();
  }
}

/** Đã bật chưa (dùng để hiển thị đúng trạng thái nút). */
export async function isPushEnabled(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  if (Notification.permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.ready;
  return (await reg.pushManager.getSubscription()) !== null;
}

/**
 * Đồng bộ lại subscription mỗi lần mở app.
 * Cần thiết vì trình duyệt/OS có thể tự làm mới endpoint (pushsubscriptionchange),
 * khi đó bản ghi trong DB đã chết mà người dùng không hề biết.
 */
export async function resyncPush(): Promise<void> {
  try {
    if (!(await isPushEnabled())) return;

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) return;

    // Khóa VAPID đã đổi ở server → subscription hiện tại là rác, đăng ký lại.
    const vapidKey = await getVapidPublicKey();
    if (vapidKey && !isSubscriptionKeyCurrent(sub, vapidKey)) {
      console.warn('[Push] Khóa VAPID đã đổi — đăng ký lại subscription.');
      await dropStaleSubscription(sub);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });
    }

    const json = sub.toJSON();
    await apiClient.post('/push/subscribe', {
      endpoint: sub.endpoint,
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
      platform: detectPlatform(),
    });
  } catch {
    // Không chặn app nếu đồng bộ lỗi
  }
}

/** Gửi thử về chính mình — IT dùng để kiểm tra sau khi cài cho cán bộ. */
export async function sendTestPush(): Promise<void> {
  await apiClient.post('/push/test');
}
